import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Table as AntdTable } from 'antd'
import { Pagination } from '../pagination/Pagination'
import { SorterResult } from 'antd/lib/table/interface'
import clsx from 'clsx'

import styles from './Table.module.scss'
import { IconButton } from '../../../../../shared/components/buttons/icon-button/IconButton'
import { TriangleButton } from '../../../../../shared/components/buttons/triangle/TriangleButton'
import {
  useAiButtonSlot,
  useAiChat,
} from '../../../../../shared/components/ai/ai-provider/AiProvider'
import { useMenuLayout } from '../../../../../app/layouts/main-layout/hooks/UseMenuLayout'
import { NoResultsStub } from '../../../../widgets/stubs/NoResultsStub'
import { StubLayout } from '../../../../widgets/stubs/StubLayout'
import {
  AppliedFiltersBoundary,
  useAppliedFilters,
} from '../../../../context/applied-filters/AppliedFiltersProvider'
import {
  STICKY_EXPANDED_ROW_CLASS,
  STICKY_EXPANDED_ROWS_CLASS,
  stickyExpandedRowsStyle,
} from '../../lib/stickyExpandedRow'

export interface TableProps {
  columns: any
  data: any
  loading: boolean
  /** Заглушка пустого списка: рисуется вместо таблицы. */
  emptyStub?: ReactNode
  /** Отступ раскладки между контентом над таблицей и ней самой — его заглушка вычитает из своего. */
  stubGap?: number
  toolbar?: ReactNode
  /** Ширина таблицы для горизонтальной прокрутки (antd scroll.x). */
  scrollX?: string | number
  /** Раскрытая строка стоит на месте при горизонтальной прокрутке и не заходит на закреплённые справа колонки. */
  stickyExpandedRow?: boolean
  showExpandIcon?: boolean
  showExpandColumn?: boolean
  rowKey?: (record: any) => any
  rowClassName?: (record: any) => string
  selectedRowKeys?: any[]
  rowSelectionDisabled?: (record: any) => boolean
  expandedRowRender?: (record: any) => ReactNode
  expandedRowKeys?: any[]
  onPaginationChange?: (page: number, pageSize: number, total: number) => void
  onRowSelectionChange?: (selection: any[]) => void
  onSortingChange?: (column: string) => void
}

export const Table = ({
  columns,
  data,
  loading,
  emptyStub,
  stubGap,
  toolbar,
  scrollX = '1200px',
  stickyExpandedRow,
  showExpandIcon = false,
  rowKey = (record: any) => record.id,
  rowClassName,
  selectedRowKeys,
  rowSelectionDisabled,
  expandedRowRender,
  expandedRowKeys,
  onPaginationChange,
  onRowSelectionChange,
  onSortingChange,
}: TableProps) => {
  const { menuLayout } = useMenuLayout()
  const aiButtonSlot = useAiButtonSlot(menuLayout === 'sidebar')
  const tableRef = useRef<HTMLDivElement | null>(null)
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const [showFixedToolbar, setShowFixedToolbar] = useState(false)
  const [fixedToolbarStyle, setFixedToolbarStyle] = useState<{
    left: number
    width: number
  }>({ left: 0, width: 0 })

  const { handleButtonVisible } = useAiChat()
  const { hasApplied: hasAppliedFilters } = useAppliedFilters()

  // при применённых фильтрах вместо zero state показываем "ничего не найдено"
  const stub = hasAppliedFilters ? <NoResultsStub /> : emptyStub

  const handlePagination = (page: number, pageSize: number, total: number) => {
    onPaginationChange && onPaginationChange(page, pageSize, total)
  }

  const handleSorting = (sorter: SorterResult<any> | SorterResult<any>[]) => {
    onSortingChange &&
      onSortingChange(
        !Array.isArray(sorter) && sorter.column
          ? `${sorter.order == 'descend' ? '-' : ''}${sorter.columnKey}`
          : '',
      )
  }

  const handleRowSelection = (selection: any[]) => {
    onRowSelectionChange && onRowSelectionChange(selection)
  }

  useEffect(() => {
    if (!toolbar || !toolbarRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFixedToolbar(!entry.isIntersecting)
        handleButtonVisible(entry.isIntersecting)
      },
      { threshold: 0, root: null, rootMargin: '0px' },
    )

    observer.observe(toolbarRef.current)

    return () => {
      handleButtonVisible(true)
      observer.disconnect()
    }
  }, [toolbar, data])

  useEffect(() => {
    if (!toolbar) return

    const handleStickyToolbar = () => {
      if (tableRef.current) {
        const tableRect = tableRef.current.getBoundingClientRect()

        setFixedToolbarStyle({
          left: tableRect.left,
          width: tableRect.width,
        })
      }
    }

    handleStickyToolbar()
    window.addEventListener('resize', handleStickyToolbar)

    return () => {
      window.removeEventListener('resize', handleStickyToolbar)
    }
  }, [toolbar, data])

  useEffect(() => {
    const rowsKeys = data?.data?.items.map(rowKey)

    if (!rowsKeys || !selectedRowKeys) return

    handleRowSelection(
      selectedRowKeys.filter((selectedRow) => rowsKeys.includes(rowKey(selectedRow))),
    )
  }, [data])

  // пустой список — заглушка вместо таблицы; пока грузится, остаётся таблица с лоадером
  if (stub && !loading && !data?.data?.items?.length)
    return <StubLayout gap={stubGap}>{stub}</StubLayout>

  return (
    <div ref={tableRef} className={clsx(styles.wrapper, 'relative flex h-full flex-col')}>
      <div className={'relative flex-1'}>
        <AntdTable
          expandable={{
            ...(showExpandIcon
              ? {
                  expandIcon: ({ expanded, onExpand, record }) => (
                    <IconButton onClick={(e) => onExpand(record, e)}>
                      <TriangleButton color='#A059FF' position={expanded ? 'top' : 'down'} />
                    </IconButton>
                  ),
                }
              : { showExpandColumn: false }),
            expandedRowRender:
              expandedRowRender &&
              ((record: any) => (
                <AppliedFiltersBoundary>{expandedRowRender(record)}</AppliedFiltersBoundary>
              )),
            ...(stickyExpandedRow && { expandedRowClassName: () => STICKY_EXPANDED_ROW_CLASS }),
            expandedRowKeys: expandedRowKeys,
          }}
          locale={{
            // пока грузится, пустое место вместо дефолтного antd «No data»
            ...(loading && { emptyText: <span /> }),
          }}
          rowSelection={
            onRowSelectionChange && {
              selectedRowKeys: selectedRowKeys?.map(rowKey),
              ...(rowSelectionDisabled && {
                getCheckboxProps: (record: any) => ({ disabled: rowSelectionDisabled(record) }),
              }),
              onChange: (selectedRowKeys, selectedRows) => {
                handleRowSelection(selectedRows)
              },
            }
          }
          className={clsx(stickyExpandedRow && STICKY_EXPANDED_ROWS_CLASS)}
          columns={columns}
          dataSource={data?.data?.items ?? []}
          loading={loading}
          pagination={false}
          rowClassName={rowClassName}
          rowKey={rowKey}
          scroll={{ x: scrollX }}
          showSorterTooltip={false}
          style={stickyExpandedRow ? stickyExpandedRowsStyle(columns) : undefined}
          tableLayout={'auto'}
          onChange={(pagination, filters, sorter) => {
            handleSorting(sorter)
          }}
        />
      </div>
      <div className={clsx('flex flex-col')}>
        {toolbar && (
          <>
            <div
              ref={toolbarRef}
              aria-hidden
              className={clsx('w-full', showFixedToolbar ? 'my-[24px] h-[52px]' : 'h-0')}
            />
            <div
              className={clsx(
                'w-full overflow-visible rounded-[12px] border-[1px] border-[#DFE5EC] bg-[#F5F9FE] px-[16px] py-[12px]',
                showFixedToolbar ? 'fixed bottom-0 z-[100] my-[24px]' : 'relative my-[24px]',
              )}
              style={showFixedToolbar ? fixedToolbarStyle : undefined}
            >
              {toolbar}
            </div>
          </>
        )}
        <div
          {...aiButtonSlot}
          data-table-pagination
          className={
            'flex min-h-[73px] w-full items-center border-t-[1px] border-[#8D7CA7]/[0.12] py-[14px]'
          }
        >
          {!!data?.data?.items?.length && (
            <Pagination
              page={data?.data?.page}
              pageCount={data?.data?.pageCount}
              pageSize={data?.data?.pageSize}
              total={data?.data?.total}
              onPageChange={(page) =>
                handlePagination(page, data?.data?.pageSize, data?.data?.pageCount)
              }
              onPageSizeChange={(size) =>
                handlePagination(data?.data?.page, size, data?.data?.pageCount)
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}
